// #618: the People screen's own data source — every human in the caller's enterprise, as `GET /v1/users`
// returns them. It lives beside `@/api` rather than inside it because who may reach the enterprise is a
// different question from the AI org chart `@/api` serves, and because a refusal here has to reach the
// screen as the API's own word for it (`not_enterprise_owner`) so the screen can explain itself.

/** One row on the People screen: who they are, whether they own the enterprise, what they hold, when they joined. */
export type EnterpriseUserRow = {
    id: string
    name: string
    avatarUrl: string | null
    isOwner: boolean
    scopes: string[] | null
    joinedAt: string | null
    /**
     * The person's own mailbox address in this enterprise (#882), null while an owner has given them none. Optional:
     * an API older than #882 sends none.
     */
    mailAddress?: string | null
}

/**
 * #626: one invitation somebody has been sent but has not accepted yet, as `GET /v1/users/invitations`
 * returns it — who it is for, what it would grant, when it lapses, and who sent it. `expiresAt` arrives as
 * an ISO string because it crossed JSON, and there is deliberately no `token` field: the raw token is a live
 * credential and the API never serves it here, so this screen has nowhere to put one even by accident.
 */
export type OutstandingInvitationRow = {
    id: string
    email: string | null
    grant: string
    /** What a member invitation would let its holder reach (#619); null for an owner invitation. */
    scopes?: string[] | null
    invitedByName: string | null
    expiresAt: string
}

/**
 * #619: who to invite, and the grant chosen for them before anything is sent — owning the enterprise
 * outright, or exactly these `<resource>:<level>` scopes (none at all is a real choice).
 */
export type InvitationRequest = { email: string; isOwner: boolean; scopes: string[] }

/**
 * An invitation at the one moment its link exists in the portal: the response that made it. Only the link's
 * hash is kept server-side, so once this is dismissed nothing can show it again. `mail` says whether the email
 * carrying it went out, and is null when no send was attempted.
 */
export type IssuedInvitation = {
    id: string
    link: string
    email: string | null
    grant: string
    scopes: string[] | null
    expiresAt: string
    mail: 'sent' | 'failed' | null
}

/**
 * A refusal from this API: `message` is the machine-readable code the screen switches on (as for every other
 * call here), and `detail` the sentence the API said it in, for a refusal whose words matter — an invitation
 * that reaches further than its sender does names exactly what overreached.
 */
export class UsersApiError extends Error {
    readonly detail: string | null

    constructor(code: string, detail: string | null) {
        super(code)
        this.name = 'UsersApiError'
        this.detail = detail
    }
}

/** A write that hands something back, refused the same way a failed read is, with the API's own sentence kept. */
async function write<T>(method: 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown): Promise<T> {
    const response = await fetch(path, {
        method,
        headers: { 'Content-Type': 'application/json' },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    })
    if (!response.ok) {
        const refusal = await readRefusal(response)
        throw new UsersApiError(refusal.code, refusal.detail)
    }
    return (await response.json()) as T
}

function post<T>(path: string, body: unknown): Promise<T> {
    return write<T>('POST', path, body)
}

async function get<T>(path: string): Promise<T> {
    const response = await fetch(path, { headers: { 'Content-Type': 'application/json' } })
    if (!response.ok) {
        throw new Error(await errorCode(response))
    }
    return (await response.json()) as T
}

/**
 * A write with nothing to hand back. The refusal is read the same way a failed read's is, so a caller can
 * explain `not_enterprise_owner` or `invitation_not_found` rather than report a number.
 */
async function del(path: string): Promise<void> {
    const response = await fetch(path, { method: 'DELETE', headers: { 'Content-Type': 'application/json' } })
    if (!response.ok) {
        throw new Error(await errorCode(response))
    }
}

/**
 * The API's own word for what went wrong (`not_enterprise_owner`), so a refusal can be explained rather
 * than reported as a number. Falls back to the status when the body says nothing a caller can act on.
 *
 * Deliberately divergent from `@/api`'s own private `request<T>()` parser: when a body carries both a string
 * `error` and a string `message`, this one prefers `error` (the machine-readable code the People screen
 * switches on) whereas `request<T>()` prefers `message` (the prose it shows verbatim). Both orders are
 * wanted — one screen explains a refusal by code, the others surface the server's sentence — so the two are
 * kept apart on purpose rather than merged. Change one and the other does not follow; check both.
 */
async function errorCode(response: Response): Promise<string> {
    return (await readRefusal(response)).code
}

/** The code `errorCode` describes, and beside it whatever sentence the body said it in. */
async function readRefusal(response: Response): Promise<{ code: string; detail: string | null }> {
    try {
        const body = (await response.json()) as { error?: unknown; message?: unknown }
        const nested =
            body.message && typeof body.message === 'object' && body.message !== null
                ? (body.message as { error?: unknown; message?: unknown })
                : undefined
        const detail = [nested?.message, body.message].find(
            (candidate): candidate is string => typeof candidate === 'string' && candidate !== '',
        )
        for (const candidate of [nested?.error, body.error, body.message]) {
            if (typeof candidate === 'string' && candidate) {
                return { code: candidate, detail: detail ?? null }
            }
        }
    } catch {
        // No JSON body, or an unreadable one: the status is all there is to report.
    }
    return { code: String(response.status), detail: null }
}

export const usersApi = {
    /** Every person in the caller's own enterprise. Refused (non-2xx) for a caller who may not manage users. */
    users: (): Promise<EnterpriseUserRow[]> => get<EnterpriseUserRow[]>('/v1/users'),

    /**
     * Everyone who has been invited to the caller's enterprise and has not joined yet (#626). A separate
     * request from `users()` because it is a separate endpoint — `GET /v1/users` keeps the shape it has
     * always had — and the People screen renders the two lists together.
     */
    invitations: (): Promise<OutstandingInvitationRow[]> => get<OutstandingInvitationRow[]>('/v1/users/invitations'),

    /**
     * Withdraws one outstanding invitation, so the link it carries stops working immediately (#626). Refused
     * with `invitation_not_found` for anything this enterprise has no outstanding invitation for, including
     * one that has just been accepted or withdrawn by somebody else.
     */
    withdrawInvitation: (id: string): Promise<void> => del(`/v1/users/invitations/${encodeURIComponent(id)}`),

    /**
     * Invites somebody at the grant chosen for them (#619), emails them the link, and hands the link back —
     * the only time it is ever handed back. Refused with a `UsersApiError` whose `detail` says why, for
     * instance naming the scopes that reach further than the sender holds.
     */
    invite: (request: InvitationRequest): Promise<IssuedInvitation> =>
        post<IssuedInvitation>('/v1/users/invitations', request),

    /**
     * One person's access history, newest first (#629): who invited them, changed their grant or removed them,
     * and when. Refused (non-2xx) for a caller who may not manage users, like `users()`.
     */
    history: (id: string): Promise<AccessChangeRow[]> =>
        get<AccessChangeRow[]>(`/v1/users/${encodeURIComponent(id)}/history`),

    /**
     * Gives a person their own mailbox address at one of the enterprise's domains, or moves them to another (#882),
     * and hands back their row as it now stands. Refused with a `UsersApiError` whose code says why:
     * `mailbox_address_invalid`, `mailbox_domain_unavailable`, `mailbox_address_taken`, or `not_enterprise_owner`.
     */
    setMailbox: (id: string, address: string): Promise<EnterpriseUserRow> =>
        write<EnterpriseUserRow>('PUT', `/v1/users/${encodeURIComponent(id)}/mailbox`, { address }),

    /** Withdraws a person's mailbox address (#882): delivery to it stops, and the row comes back without one. */
    clearMailbox: (id: string): Promise<EnterpriseUserRow> =>
        write<EnterpriseUserRow>('DELETE', `/v1/users/${encodeURIComponent(id)}/mailbox`),
}

/**
 * #629: one entry of a person's access history, as `GET /v1/users/:id/history` returns it — who changed what
 * for them, and when. `before`/`after` are the grant either side of the change (null where they held none),
 * and `createdAt` arrives as an ISO string because it crossed JSON.
 */
export type AccessChangeRow = {
    id: string
    action: string
    actorName: string | null
    subjectName: string | null
    subjectEmail: string | null
    before: { isOwner: boolean; scopes: string[] | null } | null
    after: { isOwner: boolean; scopes: string[] | null } | null
    createdAt: string
}
